import { useState, useEffect, useMemo } from 'react';
import { apiBudgetInfo, BudgetInfoResult } from '../helpers/apiHelpers';
import { useStableAccessToken } from './useStableAccessToken';
import { formatTeamNameForDeploy } from '../helpers/teamFormatter';

interface BudgetInfoState {
  message: string;
  exceeded: boolean;
  enforced?: boolean;
  formUrl?: string;
  stale?: boolean;
  metadata?: BudgetInfoResult['metadata'];
  monthlyBudget?: number;
  currentSpend?: number;
  budgetRemaining?: number;
  budgetUtilizedPercent?: number;
}

interface UseBudgetInfoParams {
  team: string;
  debouncedTeam: string;
  debouncedNamespace: string;
  isProdLike: boolean;
  deployOnBehalfOfOtherTeam: boolean;
  overrideTeam: string;
}

export function useBudgetInfo({
  team,
  debouncedTeam,
  debouncedNamespace,
  isProdLike,
  deployOnBehalfOfOtherTeam,
  overrideTeam,
}: UseBudgetInfoParams) {
  const tokenGetter = useStableAccessToken();
  const [budgetInfo, setBudgetInfo] = useState<BudgetInfoState | null>(null);

  useEffect(() => {
    if (!isProdLike && debouncedTeam && debouncedNamespace) {
      const teamForKubecost = deployOnBehalfOfOtherTeam && overrideTeam ? overrideTeam : debouncedTeam;
      const formattedTeam = teamForKubecost ? formatTeamNameForDeploy(teamForKubecost) : teamForKubecost;

      let cancelled = false;
      apiBudgetInfo(formattedTeam, tokenGetter)
        .then((result) => {
          if (cancelled) return;
          if (!result?.message) {
            setBudgetInfo(null);
            return;
          }
          setBudgetInfo({
            message: result.message,
            exceeded: result.budgetExceeded || false,
            enforced: result.enforced,
            formUrl: result.formUrl,
            stale: result.stale,
            metadata: result.metadata,
            monthlyBudget: result.monthlyBudget,
            currentSpend: result.currentSpend,
            budgetRemaining: result.budgetRemaining,
            budgetUtilizedPercent: result.budgetUtilizedPercent,
          });
        })
        .catch(() => {
          if (!cancelled) setBudgetInfo(null);
        });
      return () => { cancelled = true; };
    }
    setBudgetInfo(null);
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTeam, debouncedNamespace, isProdLike, deployOnBehalfOfOtherTeam, overrideTeam]); // tokenGetter is stable

  const budgetBannerVariant: 'info' | 'warning' | 'danger' = useMemo(() => {
    if (!budgetInfo) return 'info';
    if (budgetInfo.exceeded) return 'danger';

    const utilizedPercent = budgetInfo.budgetUtilizedPercent
      ?? (
        budgetInfo.monthlyBudget && budgetInfo.monthlyBudget > 0 && typeof budgetInfo.currentSpend === 'number'
          ? (budgetInfo.currentSpend / budgetInfo.monthlyBudget) * 100
          : 0
      );
    if (utilizedPercent >= 95) return 'danger';
    if (utilizedPercent >= 75) return 'warning';
    return 'info';
  }, [budgetInfo]);

  const budgetBannerMessage = useMemo(() => {
    if (!budgetInfo) return '';

    const hasAmounts = typeof budgetInfo.currentSpend === 'number' && typeof budgetInfo.monthlyBudget === 'number';
    const utilizedPercent = budgetInfo.budgetUtilizedPercent
      ?? (
        hasAmounts && budgetInfo.monthlyBudget! > 0
          ? (budgetInfo.currentSpend! / budgetInfo.monthlyBudget!) * 100
          : undefined
      );

    if (budgetInfo.enforced && budgetInfo.exceeded && hasAmounts) {
      return `Team ${team} has reached its monthly EOD compute budget ($${Math.round(budgetInfo.currentSpend!)} spent of $${Math.round(budgetInfo.monthlyBudget!)}). Creating new environments is blocked.`;
    }

    if (hasAmounts) {
      const spentPercent = typeof utilizedPercent === 'number' ? utilizedPercent : 0;
      const leftPercent = Math.max(100 - spentPercent, 0);
      return `Team ${team} has spent ${Math.round(spentPercent)}% from its monthly budget of $${Math.round(budgetInfo.monthlyBudget!)}. There is ${Math.round(leftPercent)}% left.`;
    }

    return budgetInfo.exceeded
      ? `Team ${team} is out of budget. ${budgetInfo.message || ''}`.trim()
      : `Team ${team}: ${budgetInfo.message}`;
  }, [budgetInfo, team]);

  const budgetCacheSubtext = useMemo(() => {
    if (!budgetInfo) return null;
    const { metadata, stale } = budgetInfo;
    const parts: string[] = [];

    if (metadata?.allocationWindowStart && metadata?.allocationWindowEnd) {
      const fmt = (iso: string) =>
        new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      parts.push(`Data covers ${fmt(metadata.allocationWindowStart)} – ${fmt(metadata.allocationWindowEnd)}`);
    }

    const ageMs = metadata?.cacheAgeMs
      ?? (metadata?.lastUpdated ? Date.now() - metadata.lastUpdated : undefined);
    if (typeof ageMs === 'number') {
      const mins = Math.round(ageMs / 60000);
      parts.push(mins < 60 ? `refreshed ${mins} min ago` : `refreshed ${Math.round(mins / 60)}h ago`);
    }

    if (stale) {
      parts.push('data may be outdated');
    }

    return parts.length > 0 ? parts.join(' · ') : null;
  }, [budgetInfo]);

  const budgetBlocked = !!(budgetInfo?.enforced && budgetInfo.exceeded);

  return { budgetInfo, budgetBlocked, budgetBannerVariant, budgetBannerMessage, budgetCacheSubtext };
}
